/**
 * @param {HTMLElement} container
 * @param {string} apiBase
 * @param {string} apiKey
 */
export function renderChat(container, apiBase, apiKey) {
  /** @type {{ conversationId?: number; messages: any[]; isLoading: boolean; ws?: WebSocket }} */
  const state = { messages: [], isLoading: false };

  const settings = loadSettings();

  const chatContainer = document.createElement('div');
  chatContainer.className = 'chat-container';
  chatContainer.innerHTML = `
    <div class="chat-sidebar">
      <h3>Conversations</h3>
      <button class="btn btn-small" id="new-chat">+ New Chat</button>
      <ul class="conversation-list" id="conversation-list"></ul>
    </div>
    <div class="chat-main">
      <div class="chat-header">
        <span id="chat-title">New Conversation</span>
        <select id="provider-select">
          <option value="anthropic">Claude 3.5</option>
          <option value="openai">GPT-4o</option>
          <option value="kimi">Kimi K2.6</option>
        </select>
      </div>
      <div class="chat-messages" id="chat-messages"></div>
      <div class="chat-input-area">
        <textarea id="chat-input" placeholder="Ask the agent to do something..." rows="2"></textarea>
        <button class="btn btn-primary" id="send-btn">Send</button>
      </div>
    </div>
  `;
  container.appendChild(chatContainer);

  const messagesEl = /** @type {HTMLElement} */ (chatContainer.querySelector('#chat-messages'));
  const inputEl = /** @type {HTMLTextAreaElement} */ (chatContainer.querySelector('#chat-input'));
  const sendBtn = /** @type {HTMLButtonElement} */ (chatContainer.querySelector('#send-btn'));
  const providerSelect = /** @type {HTMLSelectElement} */ (chatContainer.querySelector('#provider-select'));
  const newChatBtn = /** @type {HTMLButtonElement} */ (chatContainer.querySelector('#new-chat'));
  const conversationList = /** @type {HTMLElement} */ (chatContainer.querySelector('#conversation-list'));

  if (settings.default_provider) {
    providerSelect.value = settings.default_provider;
  }

  async function loadConversations() {
    try {
      const res = await fetch(`${apiBase}/api/conversations`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      const conversations = await res.json();
      conversationList.innerHTML = conversations.map((/** @type {any} */ c) => `
        <li class="conversation-item ${c.id === state.conversationId ? 'active' : ''}" data-id="${c.id}">
          <span>${escapeHtml(c.title)}</span>
          <small>${new Date(c.updated_at).toLocaleDateString()}</small>
        </li>
      `).join('');

      conversationList.querySelectorAll('.conversation-item').forEach(item => {
        item.addEventListener('click', () => {
          const id = parseInt(/** @type {HTMLElement} */ (item).dataset.id);
          loadConversation(id);
        });
      });
    } catch (e) {
      console.error('Failed to load conversations', e);
    }
  }

  async function loadConversation(id) {
    state.conversationId = id;
    state.messages = [];
    messagesEl.innerHTML = '';

    try {
      const res = await fetch(`${apiBase}/api/conversations/${id}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      const data = await res.json();
      state.messages = data.messages || [];
      renderMessages();
      loadConversations();
    } catch (e) {
      console.error('Failed to load conversation', e);
    }
  }

  function renderMessages() {
    messagesEl.innerHTML = state.messages.map(m => renderMessage(m)).join('');
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  /**
   * @param {any} message
   */
  function renderMessage(message) {
    const isUser = message.role === 'user';
    const isTool = message.role === 'tool';

    if (isTool) {
      return `<div class="message tool-message">${escapeHtml(message.content.substring(0, 200))}...</div>`;
    }

    let toolCallsHtml = '';
    if (message.tool_calls) {
      try {
        const toolCalls = typeof message.tool_calls === 'string' ? JSON.parse(message.tool_calls) : message.tool_calls;
        toolCallsHtml = `<div class="tool-calls">${toolCalls.map((/** @type {any} */ tc) => `
          <details class="tool-call">
            <summary>${escapeHtml(tc.function.name)}</summary>
            <pre>${escapeHtml(JSON.stringify(JSON.parse(tc.function.arguments), null, 2))}</pre>
          </details>
        `).join('')}</div>`;
      } catch {
        // ignore
      }
    }

    return `
      <div class="message ${isUser ? 'user-message' : 'assistant-message'}">
        <div class="message-content">${escapeHtml(message.content)}</div>
        ${toolCallsHtml}
      </div>
    `;
  }

  async function sendMessage() {
    const text = inputEl.value.trim();
    if (!text || state.isLoading) return;

    const provider = providerSelect.value;
    const apiKeyForProvider = getApiKeyForProvider(provider);

    if (!apiKeyForProvider) {
      alert(`No API key configured for ${provider}. Go to Settings to add your key.`);
      window.location.href = '/settings';
      return;
    }

    inputEl.value = '';
    state.isLoading = true;
    sendBtn.disabled = true;
    sendBtn.textContent = '...';

    const userMessage = { id: Date.now(), conversation_id: state.conversationId || 0, role: 'user', content: text, created_at: new Date().toISOString() };
    state.messages.push(userMessage);
    renderMessages();

    try {
      const res = await fetch(`${apiBase}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          message: text,
          conversation_id: state.conversationId,
          provider: provider,
          api_key: apiKeyForProvider,
        }),
      });

      const data = await res.json();
      if (data.error) {
        throw new Error(data.error);
      }
      state.conversationId = data.conversation.id;
      state.messages.push(data.message);
      renderMessages();
      loadConversations();
    } catch (e) {
      state.messages.push({
        id: Date.now(),
        conversation_id: state.conversationId || 0,
        role: 'assistant',
        content: `Error: ${e instanceof Error ? e.message : String(e)}`,
        created_at: new Date().toISOString(),
      });
      renderMessages();
    } finally {
      state.isLoading = false;
      sendBtn.disabled = false;
      sendBtn.textContent = 'Send';
    }
  }

  sendBtn.addEventListener('click', sendMessage);
  inputEl.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  });

  newChatBtn.addEventListener('click', () => {
    state.conversationId = undefined;
    state.messages = [];
    messagesEl.innerHTML = '';
    loadConversations();
  });

  loadConversations();
}

function loadSettings() {
  try {
    const raw = localStorage.getItem('harness_keys');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getApiKeyForProvider(provider) {
  const settings = loadSettings();
  switch (provider) {
    case 'anthropic': return settings.anthropic_key;
    case 'openai': return settings.openai_key;
    case 'kimi': return settings.kimi_key;
    default: return '';
  }
}

/**
 * @param {string} str
 */
function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
